import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import BrokerLayout from '@/components/BrokerLayout';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { FileText, ExternalLink, Copy, Plus, Eye, TrendingUp } from 'lucide-react';

const BrokerLandingPages = () => {
  const [pages, setPages] = useState([]);
  const [projects, setProjects] = useState([]);
  const [profile, setProfile] = useState(null);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ project_id: '', headline: '', cta_text: 'Enquire Now', seo_title: '', meta_description: '' });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [p, pr, prof] = await Promise.all([
        api.get('/broker/landing-pages'),
        api.get('/broker/projects'),
        api.get('/broker/profile').catch(() => ({ data: null }))
      ]);
      setPages(p.data);
      setProjects(pr.data.filter(p => p.status === 'published'));
      setProfile(prof.data);
    } catch (e) { console.error(e); }
  };

  const createPage = async () => {
    if (!form.project_id) { toast.error('Select a project'); return; }
    setCreating(true);
    try {
      await api.post('/landing-pages', form);
      toast.success('Landing page created!');
      setOpen(false);
      setForm({ project_id: '', headline: '', cta_text: 'Enquire Now', seo_title: '', meta_description: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create');
    } finally {
      setCreating(false);
    }
  };

  const copyUrl = (url) => {
    const fullUrl = `${window.location.origin}${url}`;
    navigator.clipboard.writeText(fullUrl);
    toast.success('URL copied to clipboard!');
  };

  return (
    <BrokerLayout profile={profile}>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-[#0F2C59]" data-testid="landing-pages-title">Landing Pages</h1>
          <p className="text-sm text-slate-500 mt-1">Auto-generated SEO-friendly URLs for your projects</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#F97316] hover:bg-[#EA580C]" disabled={projects.length === 0} data-testid="create-landing-page-btn">
              <Plus size={16} className="mr-2"/> Create Landing Page
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Landing Page</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label>Project *</Label>
                <Select value={form.project_id} onValueChange={(v) => setForm({...form, project_id: v})}>
                  <SelectTrigger data-testid="lp-project-select"><SelectValue placeholder="Select published project"/></SelectTrigger>
                  <SelectContent>
                    {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name} ({p.city})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Custom Headline</Label>
                <Input value={form.headline} onChange={(e) => setForm({...form, headline: e.target.value})} placeholder="Own your dream home at..." data-testid="lp-headline-input" />
              </div>
              <div>
                <Label>CTA Text</Label>
                <Input value={form.cta_text} onChange={(e) => setForm({...form, cta_text: e.target.value})} data-testid="lp-cta-input" />
              </div>
              <div>
                <Label>SEO Title</Label>
                <Input value={form.seo_title} onChange={(e) => setForm({...form, seo_title: e.target.value})} placeholder="Best Apartments in Gurugram | PropertyByKilo" data-testid="lp-seo-title-input" />
              </div>
              <div>
                <Label>Meta Description</Label>
                <Textarea value={form.meta_description} onChange={(e) => setForm({...form, meta_description: e.target.value})} rows={3} data-testid="lp-meta-input" />
              </div>
              <Button onClick={createPage} disabled={creating} className="w-full bg-[#0F2C59] hover:bg-[#0F2C59]/90" data-testid="lp-submit-btn">
                {creating ? 'Creating...' : 'Generate Landing Page'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {projects.length === 0 && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
          You need at least one published project to create a landing page. Get admin approval first.
        </div>
      )}

      {pages.length === 0 ? (
        <div className="bg-white rounded-lg border border-dashed border-slate-300 p-12 text-center">
          <FileText size={40} className="mx-auto text-slate-300 mb-3" />
          <div className="text-slate-500 font-medium">No landing pages yet</div>
          <div className="text-xs text-slate-400 mt-1">Create one to start capturing leads</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {pages.map(page => (
            <div key={page.id} className="bg-white rounded-lg border border-slate-200 p-5" data-testid={`landing-page-${page.id}`}>
              <div className="flex items-start justify-between">
                <h3 className="font-bold text-slate-900">{page.seo_title || page.headline || 'Landing Page'}</h3>
                <span className={`text-xs px-2 py-0.5 rounded-md font-medium ${page.published ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                  {page.published ? 'Live' : 'Draft'}
                </span>
              </div>

              <div className="mt-3 flex items-center gap-2 p-2 bg-slate-50 rounded-md">
                <code className="text-xs font-mono text-slate-700 flex-1 truncate">{page.unique_url}</code>
                <button onClick={() => copyUrl(page.unique_url)} className="text-slate-500 hover:text-[#F97316]" data-testid={`copy-url-${page.id}`}><Copy size={14}/></button>
                <Link to={page.unique_url} target="_blank" className="text-slate-500 hover:text-[#F97316]" data-testid={`open-url-${page.id}`}><ExternalLink size={14}/></Link>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-md bg-blue-50 flex items-center justify-center"><Eye size={14} className="text-blue-600"/></div>
                  <div>
                    <div className="text-xs text-slate-500">Views</div>
                    <div className="font-bold text-slate-900">{page.views}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-md bg-emerald-50 flex items-center justify-center"><TrendingUp size={14} className="text-emerald-600"/></div>
                  <div>
                    <div className="text-xs text-slate-500">Leads</div>
                    <div className="font-bold text-slate-900">{page.leads_generated}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </BrokerLayout>
  );
};

export default BrokerLandingPages;
